import React, { useEffect, useState, useContext, useCallback } from 'react';
import { ref, onValue, update, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import AuthContext from '../AuthContext';
import { formatLocalTime, hasGameStarted } from '../utils/timezoneUtils';
import { FaLock, FaClock } from 'react-icons/fa'; // Import the FaLock and FaClock icons
import TeamName from './TeamName';
import './GameList.css'; // Import the CSS file
import autoLockService from './AutoLockService';
import { DateTime } from 'luxon';
import { useLiveScores } from '../hooks/useLiveScores';
const formatSpread = (spread) => (spread > 0 ? `+${spread}` : spread);

const formatLockDisplay = (lockDateTime) => (
  lockDateTime.toLocal().toFormat('ccc h:mma').replace('AM', 'am').replace('PM', 'pm')
);

const getNextLockLabel = (games) => {
  const now = DateTime.utc();
  const next = games
    .filter((game) => !game.locked && game.dateTime)
    .map((game) => DateTime.fromISO(game.dateTime).minus({ minutes: 5 }))
    .filter((lock) => lock > now)
    .sort((a, b) => a.toMillis() - b.toMillis())[0];

  if (!next) return null;
  return formatLockDisplay(next);
};

const groupGamesByWeekAndDay = (games) => {
  const sorted = [...games].sort((a, b) => {
    const weekDiff = (Number(a.weekNumber) || 0) - (Number(b.weekNumber) || 0);
    if (weekDiff !== 0) return weekDiff;
    const aTime = a.dateTime ? DateTime.fromISO(a.dateTime).toMillis() : Number.MAX_SAFE_INTEGER;
    const bTime = b.dateTime ? DateTime.fromISO(b.dateTime).toMillis() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  const weeks = [];
  sorted.forEach((game) => {
    const weekNumber = Number(game.weekNumber) || 0;
    let weekGroup = weeks.find((week) => week.weekNumber === weekNumber);
    if (!weekGroup) {
      weekGroup = { weekNumber, days: [] };
      weeks.push(weekGroup);
    }

    const local = game.dateTime ? DateTime.fromISO(game.dateTime).toLocal() : null;
    const dayKey = local ? local.toFormat('yyyy-MM-dd') : 'unscheduled';
    const dayLabel = local ? local.toFormat('EEEE, MMM d') : 'Time TBD';
    let dayGroup = weekGroup.days.find((day) => day.key === dayKey);
    if (!dayGroup) {
      dayGroup = { key: dayKey, label: dayLabel, games: [] };
      weekGroup.days.push(dayGroup);
    }
    dayGroup.games.push(game);
  });

  return weeks;
};

const LiveStatus = ({ liveScore }) => {
  if (liveScore.stale) {
    return <span className="text-gray-400 font-semibold">Score delayed</span>;
  }
  return liveScore.state === 'in' ? (
    <span className="text-green-400 font-semibold">
      <span className="live-indicator" />
      {liveScore.detail}
    </span>
  ) : (
    <span className="text-gray-300 font-semibold">{liveScore.detail}</span>
  );
};

const TeamScore = ({ score, opponentScore }) => (
  <span
    className={`ml-auto pl-2 font-semibold tabular-nums ${
      Number(score) >= Number(opponentScore) ? 'text-white' : 'text-gray-400'
    }`}
  >
    {score}
  </span>
);

const GameCard = ({
  game,
  liveScore,
  picks,
  weekTriplePlay,
  games,
  countdownTimers,
  handlePickChange,
  handleTriplePlayChange,
}) => {
  const triplePlayLocked = game.locked || Boolean(
    weekTriplePlay[game.weekNumber] && games.find((g) => g.id === weekTriplePlay[game.weekNumber])?.locked
  );
  const showScore = liveScore && liveScore.state !== 'pre';

  return (
    <div className="bg-gray-800 p-4 rounded shadow-md">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="text-sm text-gray-400">
          {showScore ? (
            <LiveStatus liveScore={liveScore} />
          ) : (
            game.dateTime ? formatLocalTime(game.dateTime, 'h:mm a') : 'Time TBD'
          )}
          {game.location ? ` · ${game.location}` : ''}
        </div>
        {game.locked ? <FaLock className="text-red-500 flex-shrink-0 mt-0.5" /> : null}
      </div>
      {!game.locked && countdownTimers[game.id] ? (
        <div className={`mb-2 text-sm flex items-center ${countdownTimers[game.id].timeUntilLock === 'Locking soon...' ? 'countdown-timer lock-warning' : 'text-yellow-400'}`}>
          <FaClock className="mr-1" />
          {countdownTimers[game.id].timeUntilLock}
        </div>
      ) : null}
      <div className="flex justify-between items-center gap-3">
        <div className="flex flex-col min-w-0 flex-1">
          <label className={`py-1 flex items-center ${picks[game.id]?.pick === 'away' ? 'selected-option' : ''}`}>
            <input
              type="radio"
              name={game.id}
              value="away"
              onChange={() => handlePickChange(game.id, 'away')}
              checked={picks[game.id]?.pick === 'away'}
              disabled={game.locked}
              className={`mr-2 ${game.locked ? 'radio-disabled' : ''}`}
              style={{ transform: 'scale(1.5)' }}
            />
            <TeamName name={`${game.awayTeam} (${formatSpread(game.awaySpread)})`} logo={game.awayLogo} />
            {showScore ? <TeamScore score={liveScore.awayScore} opponentScore={liveScore.homeScore} /> : null}
          </label>
          <label className={`py-1 flex items-center ${picks[game.id]?.pick === 'home' ? 'selected-option' : ''}`}>
            <input
              type="radio"
              name={game.id}
              value="home"
              onChange={() => handlePickChange(game.id, 'home')}
              checked={picks[game.id]?.pick === 'home'}
              disabled={game.locked}
              className={`mr-2 ${game.locked && picks[game.id]?.pick === 'home' ? 'checked-disabled' : ''}`}
              style={{ transform: 'scale(1.5)' }}
            />
            <TeamName name={`${game.homeTeam} (${formatSpread(game.homeSpread)})`} logo={game.homeLogo} />
            {showScore ? <TeamScore score={liveScore.homeScore} opponentScore={liveScore.awayScore} /> : null}
          </label>
        </div>
        <label className={`py-1 flex items-center flex-shrink-0 ${weekTriplePlay[game.weekNumber] === game.id ? 'selected-option' : ''}`}>
          <input
            type="checkbox"
            onChange={() => handleTriplePlayChange(game.id, game.weekNumber)}
            checked={weekTriplePlay[game.weekNumber] === game.id}
            disabled={triplePlayLocked}
            className={`mr-2 ${triplePlayLocked && weekTriplePlay[game.weekNumber] === game.id ? 'checked-disabled' : ''}`}
            style={{ transform: 'scale(1.5)' }}
          />
          3x
        </label>
      </div>
    </div>
  );
};

const GameList = () => {
  const { currentUser } = useContext(AuthContext);
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState({});
  const [lastSubmittedPicks, setLastSubmittedPicks] = useState({}); // Store last submitted picks for change detection
  const [weekTriplePlay, setWeekTriplePlay] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [allPicksExist, setAllPicksExist] = useState(false);
  const [users, setUsers] = useState([]); // State to hold the list of users
  const [selectedUser, setSelectedUser] = useState(''); // State to hold the selected user
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [countdownTimers, setCountdownTimers] = useState({});
  const [lockNotification, setLockNotification] = useState(null);
  const liveScores = useLiveScores(games);

  useEffect(() => {
    const fetchGames = () => {
      const gamesRef = ref(database, 'games');
      onValue(gamesRef, (snapshot) => {
        const data = snapshot.val();
        const gamesList = data ? Object.keys(data).map((id) => ({ id, ...data[id] })).filter(game => game.active) : [];
        setGames(gamesList);

        if (currentUser) {
          const picksRef = ref(database, `picks/${currentUser.uid}`);
          onValue(picksRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
              setPicks(data);
              setLastSubmittedPicks(data); // Store last submitted picks for change detection
              setAllPicksExist(gamesList.every(game => data[game.id]));

              // Set weekTriplePlay based on saved picks
              const triplePlay = {};
              gamesList.forEach(game => {
                if (data[game.id]?.triplePlay) {
                  triplePlay[game.weekNumber] = game.id;
                }
              });
              setWeekTriplePlay(triplePlay);
            }
          });
        }
      });
    };

    const fetchUsers = () => {
      const usersRef = ref(database, 'users');
      onValue(usersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const usersList = Object.keys(data).map((id) => ({ id, ...data[id] }));
          usersList.sort((a, b) => a.name.localeCompare(b.name));
          setUsers(usersList);
        }
      });
    };

    fetchGames();

    if (currentUser) {
      const userRef = ref(database, `users/${currentUser.uid}`);
      onValue(userRef, (snapshot) => {
        const data = snapshot.val();
        if (data?.admin) {
          setIsAdmin(true);
          fetchUsers();
        }
      });
    }

    // Cleanup function
    return () => {
      // Firebase onValue cleanup is handled automatically
    };
  }, [currentUser]);

  // Memoized countdown timer update function
  const updateCountdownTimers = useCallback(() => {
    const timers = {};
    games.forEach((game) => {
      if (game.dateTime && !game.locked) {
        const lockStatus = autoLockService.getGameLockStatus(game.dateTime);
        timers[game.id] = lockStatus;
      }
    });
    setCountdownTimers(timers);
  }, [games]);

  // Memoized games locked handler
  const handleGamesLocked = useCallback((event) => {
    setLockNotification({
      message: `${event.detail.count} game(s) have been automatically locked!`,
      timestamp: event.detail.timestamp
    });
    
    // Clear notification after 5 seconds
    setTimeout(() => {
      setLockNotification(null);
    }, 5000);
    
    // Update countdown timers immediately
    updateCountdownTimers();
  }, [updateCountdownTimers]);

  // Separate effect for countdown timers that updates when games change
  useEffect(() => {
    if (games.length === 0) return;

    // Update countdown timers every minute
    const countdownInterval = setInterval(updateCountdownTimers, 60000);
    
    // Initial update
    updateCountdownTimers();

    // Listen for games being locked
    window.addEventListener('gamesLocked', handleGamesLocked);

    // Cleanup function
    return () => {
      clearInterval(countdownInterval);
      window.removeEventListener('gamesLocked', handleGamesLocked);
    };
  }, [games, updateCountdownTimers, handleGamesLocked]);

  const handlePickChange = (gameId, pick) => {
    const weekNumber = games.find(game => game.id === gameId).weekNumber;
    setPicks({
      ...picks,
      [gameId]: { pick, triplePlay: weekTriplePlay[weekNumber] === gameId },
    });
  };

  const handleTriplePlayChange = (gameId, weekNumber) => {
    // Check if the current triple play game for this week is locked
    const currentTriplePlayGameId = weekTriplePlay[weekNumber];
    if (currentTriplePlayGameId) {
      const currentTriplePlayGame = games.find(g => g.id === currentTriplePlayGameId);
      if (currentTriplePlayGame && currentTriplePlayGame.locked) {
        // Don't allow changing triple play if the current selection is locked
        return;
      }
    }

    // Find the game to check if it's locked
    const game = games.find(g => g.id === gameId);
    if (game && game.locked) {
      // Don't allow changes to locked games
      return;
    }

    setWeekTriplePlay((prevTriplePlay) => {
      const newTriplePlay = { ...prevTriplePlay };

      // Remove triple play from any other game in the same week
      Object.keys(newTriplePlay).forEach((week) => {
        if (week === weekNumber.toString() && newTriplePlay[week] !== gameId) {
          delete newTriplePlay[week];
        }
      });

      // Toggle triple play for the selected game
      if (newTriplePlay[weekNumber] === gameId) {
        delete newTriplePlay[weekNumber];
      } else {
        newTriplePlay[weekNumber] = gameId;
      }

      // Update picks to reflect the triple play change immediately
      setPicks((prevPicks) => ({
        ...prevPicks,
        [gameId]: {
          ...prevPicks[gameId],
          triplePlay: newTriplePlay[weekNumber] === gameId, // Set triplePlay based on current selection
        },
      }));

      return newTriplePlay;
    });
  };

  const calculateScores = (picks, games) => {
    let scores = {};
    games.forEach((game) => {
      const gameId = game.id;
      const pick = picks[gameId];
      if (pick && game.winner) { // Only calculate score if the game has a winner
        const isCorrectPick = game.winner === 'push' || (pick.pick === 'home' && game.winner === 'home') || (pick.pick === 'away' && game.winner === 'away');
        const score = isCorrectPick ? 1 : 0;
        if (pick.triplePlay) {
          scores[gameId] = score * 3; // Triple play scores triple points
        } else {
          scores[gameId] = score;
        }
      }
    });
    return scores;
  };

  // Check if a pick has changed from its last submitted value
  const hasPickChanged = (gameId) => {
    const currentPick = picks[gameId];
    const lastSubmittedPick = lastSubmittedPicks[gameId];
    
    // If no current pick, it hasn't changed
    if (!currentPick) return false;
    
    // If no last submitted pick, this is a new pick (changed)
    if (!lastSubmittedPick) return true;
    
    // Compare pick and triplePlay values
    return currentPick.pick !== lastSubmittedPick.pick || 
           currentPick.triplePlay !== lastSubmittedPick.triplePlay;
  };

  const submitPicks = async () => {
    // Check if a triple play is selected for each week
    const weeks = new Set(games.map(game => game.weekNumber));
    const triplePlayWeeks = new Set(Object.keys(weekTriplePlay).map(Number));

    if (weeks.size !== triplePlayWeeks.size) {
      setSubmissionStatus({ type: 'error', message: `Error submitting picks: Please select a Triple Play for each week before submitting.` });
      return;
    }

    if (!currentUser) {
      setSubmissionStatus({ type: 'error', message: 'You need to log in to submit picks.' });
      return;
    }

    const userId = selectedUser || currentUser.uid; // Use selected user if available, otherwise use current user
    const userEmail = selectedUser ? users.find(user => user.id === selectedUser).email : currentUser.email;

    // Requery the database for the latest game data
    const gamesRef = ref(database, 'games');
    const gamesSnapshot = await get(gamesRef);
    const latestGames = gamesSnapshot.val() ? Object.keys(gamesSnapshot.val()).map((id) => ({ id, ...gamesSnapshot.val()[id] })) : [];

    // Fetch current picks from database to get up-to-date updatedAt fields
    const currentPicksRef = ref(database, `picks/${userId}`);
    const currentPicksSnapshot = await get(currentPicksRef);
    const currentPicksFromDB = currentPicksSnapshot.val() || {};


    const allGamesLocked = latestGames.every(game => game.locked);
    if (allGamesLocked) {
      setSubmissionStatus({ type: 'error', message: 'All picks are locked.' });
      setTimeout(() => {
        setSubmissionStatus(null);
      }, 3000);
      return;
    }

    const updateData = {};

    // Update picks and include all games (active and inactive) except those that have started
    latestGames.forEach((game) => {
      if (picks[game.id] && game.active && !hasGameStarted(game.dateTime)) {
        // Ensure triplePlay is correctly set based on weekTriplePlay
        const weekNumber = game.weekNumber;
        const isTriplePlay = weekTriplePlay[weekNumber] === game.id;
        
        // Only add updatedAt timestamp if the pick has actually changed
        const pickData = { 
          ...picks[game.id], 
          triplePlay: isTriplePlay
        };
        
        if (hasPickChanged(game.id)) {
          pickData.updatedAt = DateTime.utc().toISO();
        } else {
          // Preserve existing updatedAt timestamp if no changes were made
          const currentPickFromDB = currentPicksFromDB[game.id];
          if (currentPickFromDB && currentPickFromDB.updatedAt) {
            pickData.updatedAt = currentPickFromDB.updatedAt;
          }
        }
        
        updateData[`picks/${userId}/${game.id}`] = pickData;
        updateData[`picks/${userId}/email`] = userEmail;
      }
    });

    // Fetch existing scores
    const scoresRef = ref(database, `scores/${userId}`);
    const scoresSnapshot = await get(scoresRef);
    const existingScores = scoresSnapshot.val() || {};

    // Calculate new scores
    const newScores = calculateScores(picks, latestGames);

    // Merge new scores with existing scores
    const mergedScores = { ...existingScores, ...newScores };
    updateData[`scores/${userId}`] = mergedScores;

    try {
      await update(ref(database), updateData);
      
      // Update lastSubmittedPicks to reflect the new baseline after successful submission
      setLastSubmittedPicks({ ...picks });
      
      setSubmissionStatus({
        type: 'success',
        message: 'Picks Updated',
      });

      setTimeout(() => {
        setSubmissionStatus(null);
      }, 3000);
    } catch (error) {
      setSubmissionStatus({ type: 'error', message: `Error submitting picks: ${error.message}` });
    }
  };

  const pickedCount = games.filter((game) => Boolean(picks[game.id]?.pick)).length;
  const nextLockLabel = getNextLockLabel(games);
  const allLocked = games.length > 0 && games.every((game) => game.locked);
  const statusParts = [`${pickedCount} of ${games.length}`];
  if (allLocked) {
    statusParts.push('all locked');
  } else if (nextLockLabel) {
    statusParts.push(`next lock ${nextLockLabel}`);
  }
  const groupedGames = groupGamesByWeekAndDay(games);
  const showWeekHeaders = groupedGames.length > 1;

  return (
    <div className="relative max-w-md mx-auto pt-4 pb-24">
      {submissionStatus && (
        <div className={`fixed top-0 left-0 right-0 p-4 z-50 ${submissionStatus.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white text-center`}>
          {submissionStatus.message}
        </div>
      )}
      {lockNotification && (
        <div className="fixed top-0 left-0 right-0 p-4 z-50 bg-yellow-500 text-gray-800 text-center font-semibold">
          {lockNotification.message}
        </div>
      )}
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-2xl font-bold text-accent pl-4">Game List</h2>
        {currentUser && isAdmin && (
          <button
            onClick={(e) => {
              e.preventDefault();
              if (showUserSelect) {
                setSelectedUser('');
              }
              setShowUserSelect(!showUserSelect);
            }}
            className="mr-2 text-yellow-500 px-4 py-2 rounded-md cursor-pointer outline outline-1 outline-yellow-500"
          >
            {showUserSelect ? 'pick for myself' : 'pick for someone else'}
          </button>
        )}
      </div>
      {games.length > 0 ? (
        <p className="text-sm text-gray-400 pl-4 mb-4">{statusParts.join(' · ')}</p>
      ) : null}

      {currentUser && isAdmin && showUserSelect && (
        <div className="mb-4 px-4">
          <label htmlFor="userSelect" className="block text-accent">Select User:</label>
          <select
            id="userSelect"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="mt-1 bg-gray-800 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            <option value="">-- Select a user --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </select>
        </div>
      )}
      {games.length === 0 ? (
        <div className="bg-gray-800 p-6 rounded text-center text-gray-400 mx-4">
          No games posted yet. Check back once this week&apos;s slate is up.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 px-4">
          {groupedGames.map((weekGroup) => (
            <div key={weekGroup.weekNumber} className="grid grid-cols-1 gap-4">
              {showWeekHeaders ? (
                <h3 className="text-sm font-semibold text-accent tracking-wide">Week {weekGroup.weekNumber}</h3>
              ) : null}
              {weekGroup.days.map((dayGroup) => (
                <div key={dayGroup.key} className="grid grid-cols-1 gap-3">
                  <h4 className="text-xl font-semibold text-white">{dayGroup.label}</h4>
                  {dayGroup.games.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      liveScore={liveScores[game.id]}
                      picks={picks}
                      weekTriplePlay={weekTriplePlay}
                      games={games}
                      countdownTimers={countdownTimers}
                      handlePickChange={handlePickChange}
                      handleTriplePlayChange={handleTriplePlayChange}
                    />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {games.length > 0 ? (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 w-full max-w-md px-4 py-3 z-40 bg-gray-900/95 border-t border-gray-700">
          <button
            onClick={submitPicks}
            className="w-full bg-accent text-gray-800 px-4 py-3 rounded hover:bg-accent-dark font-semibold"
          >
            {allPicksExist ? 'Update Picks' : 'Submit Picks'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default GameList;
